import { useState, useEffect, useCallback, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { selectedSiteAtom } from '../state/sitesAtom';
import { campaignsApi } from '../api';

/**
 * Hook for fetching all ads (across campaigns)
 */
export function useAds(filters) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialize filters for stable dependency
  const filtersKey = JSON.stringify(filters || {});
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { start, end } = filtersRef.current || {};
      const result = await campaignsApi.getAllAds({ start, end });
      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, filtersKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching ads for a specific campaign
 */
export function useCampaignAds(campaignId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!campaignId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await campaignsApi.getAds(campaignId);
      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for ad mutations
 */
export function useAdMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createAd = useCallback(async (campaignId, formData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await campaignsApi.createAd(campaignId, formData);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAd = useCallback(async (campaignId, adId, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await campaignsApi.updateAd(campaignId, adId, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAd = useCallback(async (campaignId, adId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await campaignsApi.deleteAd(campaignId, adId);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const duplicateAd = useCallback(async (campaignId, adId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await campaignsApi.duplicateAd(campaignId, adId);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createAd,
    updateAd,
    deleteAd,
    duplicateAd,
    loading,
    error,
  };
}

export default useAds;
